let pendingAuth: Promise<string | null> | null = null;

/**
 * Opens the login URL in an Office dialog and waits for the auth flow to
 * complete. The dialog redirects through the OIDC provider and back to the
 * app with `?officeAuthCallback=true`. The dialog-callback handler then
 * reads the token and sends it back via `messageParent`.
 *
 * Deduplicates concurrent calls — if a dialog is already open, callers
 * share the same promise.
 */
export function authenticateViaDialog(loginUrl: string): Promise<string | null> {
  if (pendingAuth) {
    return pendingAuth;
  }

  pendingAuth = new Promise<string | null>((resolve) => {
    const dialogUrl = buildDialogUrl(loginUrl);

    Office.context.ui.displayDialogAsync(dialogUrl, { height: 60, width: 40 }, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        console.error("Office dialog failed:", { error: result.error.message, url: dialogUrl });
        pendingAuth = null;
        resolve(null);
        return;
      }

      const dialog = result.value;

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        dialog.close();
        pendingAuth = null;

        try {
          const message = JSON.parse((arg as { message: string }).message);
          resolve(message.token ?? null);
        } catch {
          resolve(null);
        }
      });

      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
        // User closed the dialog manually
        pendingAuth = null;
        resolve(null);
      });
    });
  });

  return pendingAuth;
}

/**
 * Appends `officeAuthCallback=true` to the return URL so the dialog
 * callback handler can detect it loaded after the OIDC redirect.
 */
function buildDialogUrl(loginUrl: string): string {
  const url = new URL(loginUrl);
  const returnUrl = url.searchParams.get("return_url") ?? url.searchParams.get("returnUrl");

  if (returnUrl) {
    const returnUrlObj = new URL(returnUrl);
    returnUrlObj.searchParams.set("officeAuthCallback", "true");

    // Replace the return_url param with the modified one
    if (url.searchParams.has("return_url")) {
      url.searchParams.set("return_url", returnUrlObj.toString());
    } else {
      url.searchParams.set("returnUrl", returnUrlObj.toString());
    }
  } else {
    // If no return URL, append to the login URL itself as fallback
    url.searchParams.set("officeAuthCallback", "true");
  }

  return url.toString();
}
